import React, { useRef, useEffect, useMemo } from 'react';
import Chart from 'chart.js/auto';
import { formatCurrency } from '../Utils/chartUtils';
import './Chart.css';
import { useTheme } from '../contexts/ThemeContext';

interface LoanPayment {
  month: number;
  payment: number;
  principal: number;
  interest: number;
  balance: number;
}

interface AutoLoanChartProps {
  paymentSchedule: LoanPayment[];
}

const AutoLoanChart: React.FC<AutoLoanChartProps> = ({ paymentSchedule }) => {
  const { actualTheme } = useTheme();
  const isDark = actualTheme === 'dark';
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<Chart | null>(null);

  const chartData = useMemo(() => {
    if (!paymentSchedule || paymentSchedule.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }

    const labels = paymentSchedule.map(payment => `Month ${payment.month}`);
    const balanceData = paymentSchedule.map(payment => payment.balance);
    const principalData = paymentSchedule.map(payment => payment.principal);
    const interestData = paymentSchedule.map(payment => payment.interest);

    return {
      labels,
      datasets: [
        {
          label: 'Remaining Balance',
          data: balanceData,
          borderColor: isDark ? 'rgba(96, 165, 250, 1)' : 'rgba(37, 99, 235, 1)',
          backgroundColor: isDark ? 'rgba(96, 165, 250, 0.1)' : 'rgba(37, 99, 235, 0.1)',
          borderWidth: 3,
          fill: false,
          tension: 0.1,
          yAxisID: 'y'
        },
        {
          label: 'Principal Payment',
          data: principalData,
          borderColor: isDark ? 'rgba(52, 211, 153, 1)' : 'rgba(22, 163, 74, 1)',
          backgroundColor: isDark ? 'rgba(52, 211, 153, 0.1)' : 'rgba(22, 163, 74, 0.1)',
          borderWidth: 2,
          fill: false,
          tension: 0.1,
          yAxisID: 'y1'
        },
        {
          label: 'Interest Payment',
          data: interestData,
          borderColor: isDark ? 'rgba(248, 113, 113, 1)' : 'rgba(220, 38, 38, 1)',
          backgroundColor: isDark ? 'rgba(248, 113, 113, 0.1)' : 'rgba(220, 38, 38, 0.1)',
          borderWidth: 2,
          fill: false,
          tension: 0.1,
          yAxisID: 'y1'
        }
      ]
    };
  }, [paymentSchedule, isDark]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: isDark ? '#e5e7eb' : '#374151',
          font: {
            size: 12,
            weight: 'normal' as const
          },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'line'
        }
      },
      tooltip: {
        backgroundColor: isDark ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255, 255, 255, 0.9)',
        titleColor: isDark ? '#f9fafb' : '#111827',
        bodyColor: isDark ? '#e5e7eb' : '#374151',
        borderColor: isDark ? '#4b5563' : '#d1d5db',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          label: function(context: any) {
            const value = context.raw;
            const label = context.dataset.label || '';
            return `${label}: ${formatCurrency(value)}`;
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Payment Period',
          color: isDark ? '#e5e7eb' : '#374151',
          font: {
            size: 12,
            weight: 'bold' as const
          }
        },
        ticks: {
          color: isDark ? '#9ca3af' : '#6b7280',
          font: {
            size: 11
          },
          maxTicksLimit: 12
        },
        grid: {
          color: isDark ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.8)',
          lineWidth: 1
        }
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: {
          display: true,
          text: 'Remaining Balance ($)',
          color: isDark ? '#e5e7eb' : '#374151',
          font: {
            size: 12,
            weight: 'bold' as const
          }
        },
        ticks: {
          color: isDark ? '#9ca3af' : '#6b7280',
          font: {
            size: 11
          },
          callback: function(value: any) {
            return formatCurrency(value);
          }
        },
        grid: {
          color: isDark ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.8)',
          lineWidth: 1
        }
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        title: {
          display: true,
          text: 'Payment Amount ($)',
          color: isDark ? '#e5e7eb' : '#374151',
          font: {
            size: 12,
            weight: 'bold' as const
          }
        },
        ticks: {
          color: isDark ? '#9ca3af' : '#6b7280',
          font: {
            size: 11
          },
          callback: function(value: any) {
            return formatCurrency(value);
          }
        },
        grid: {
          drawOnChartArea: false,
        },
      }
    },
    elements: {
      point: {
        radius: 0,
        hoverRadius: 6,
        hitRadius: 10
      }
    }
  }), [isDark]);

  useEffect(() => {
    if (!chartRef.current || !paymentSchedule.length) return;

    // Destroy existing chart instance
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    // Create new chart instance
    chartInstanceRef.current = new Chart(chartRef.current, {
      type: 'line',
      data: chartData,
      options: chartOptions
    });

    // Cleanup function
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [chartData, chartOptions, paymentSchedule]);

  if (!paymentSchedule || paymentSchedule.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-lg font-medium text-foreground mb-1">
            No data to display
          </div>
          <div className="text-sm text-muted-foreground">
            Enter your loan details to see the payment schedule
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-96">
      <canvas ref={chartRef} />
    </div>
  );
};

export default AutoLoanChart;